/**
 * Pure bill grouping/classification used by the Gastos tabs.
 *
 * Lives in its own leaf module so AssetBills and PeriodicBillGroups can both
 * import it without forming an import cycle (AssetBills renders
 * PeriodicBillGroups, so the helpers cannot live in AssetBills).
 */

export const isPeriodicBill = (bill) => bill?.type === 'Periodico';

/**
 * Partitions bills into the two tabs. Tests for 'Periodico' and treats
 * everything else as eventual on purpose: that way the buckets always
 * partition the input, so a legacy row with a missing or unexpected `type`
 * still shows up somewhere instead of vanishing from both tabs.
 */
export const splitBillsByType = (rows = []) => ({
	periodic: rows.filter(isPeriodicBill),
	eventual: rows.filter((bill) => !isPeriodicBill(bill)),
});

/** Still to pay. Same rule the Pendientes stat uses, so the two never disagree. */
export const isBillPending = (bill) => !bill?.paid && !bill?.confirmed;

/** Bucket for periodic bills with no template (legacy rows, or a deleted template). */
export const NO_TEMPLATE_KEY = '__sin-plantilla__';

const templateIdOf = (bill) => {
	const t = bill?.billTemplate;
	if (!t) return null;
	return (typeof t === 'object' ? t._id : t) || null;
};

/**
 * Groups periodic bills by the template that generates them, so a recurring
 * gasto shows as one row instead of one row per period.
 *
 * Bills without a resolvable template fall into NO_TEMPLATE_KEY rather than
 * being dropped — same property the Eventual/Periódico split relies on: every
 * bill lands in exactly one group and stays actionable.
 */
export const groupBillsByTemplate = (rows = []) => {
	const groups = new Map();
	rows.forEach((bill) => {
		const key = templateIdOf(bill) || NO_TEMPLATE_KEY;
		if (!groups.has(key)) {
			const template = key === NO_TEMPLATE_KEY || typeof bill.billTemplate !== 'object'
				? null
				: bill.billTemplate;
			groups.set(key, { key, template, bills: [] });
		}
		groups.get(key).bills.push(bill);
	});

	return [...groups.values()]
		.map((group) => {
			const pendingBills = group.bills.filter(isBillPending);
			const nextExpiration = pendingBills
				.map((bill) => bill.expirationDate)
				.filter(Boolean)
				.sort()[0] || null;
			return {
				...group,
				name: group.template?.name || group.bills[0]?.name || 'Sin plantilla',
				pending: pendingBills.length,
				pendingTotal: pendingBills.reduce((sum, bill) => sum + (bill.amount || 0), 0),
				nextExpiration,
			};
		})
		// Alphabetical, with the orphan bucket last so it reads as a footnote.
		.sort((a, b) => {
			if (a.key === NO_TEMPLATE_KEY) return 1;
			if (b.key === NO_TEMPLATE_KEY) return -1;
			return a.name.localeCompare(b.name, 'es');
		});
};
